import { useRef, useCallback } from 'react'
import { saveRawAudio } from '../engines/offline_storage'
import { collapseRepeatedPhrases, mergeFinalSegments } from '../utils/textSanitize'
import { productionDictionaries } from '../engines/data/production_dictionaries'
import { VoskSpeechEngine } from '../infrastructure/speech/VoskSpeechEngine'
import type { SpeechDiagnosticEvent } from '../infrastructure/speech/SpeechEngine'
import { VOSK_MODEL_URL } from '../presentation/speech/voskModel'
import { voskModelStore } from '../presentation/speech/voskModelStore'
import { sttDiagStore } from '../presentation/speech/sttDiagStore'
import { checkpoint, logError, logWarn } from '../infrastructure/debug/AppDebugLog'

/**
 * useDictation — Hybrid STT: Deepgram Live Streaming (online) z automatycznym
 * fallbackiem na natywny Web Speech API (`webkitSpeechRecognition`), gdy chmura
 * jest niedostępna. Bez tego dyktowanie w ogóle nie działało bez internetu —
 * warmup() wisiał 8s na timeoucie połączenia z Deepgram i kończył się samym
 * `onError`, bez żadnej notatki.
 *
 * Flow (silnik Deepgram):
 *  1. warmup() → natychmiast po dotknięciu: mikrofon + WS + nagrywanie w closure
 *     audio buforowane od pierwszej chwili, requestData() co 150ms (iOS/Android fix)
 *  2. start()  → noop (warmup już uruchomił nagrywanie)
 *  3. cancel() → krótki tap: zamknij audio bez onDone
 *  4. stop()   → recorder.onstop → CloseStream → WS zamknięty w tle 6s → onDone
 *
 * Kluczowe właściwości:
 *  - WS lokalny per sesja w closure → stary WS dobiega w tle, nowa sesja startuje natychmiast
 *  - Session ID (sid) → sygnały/callbacks tylko dla aktywnej sesji
 *  - getUserMedia z wyłączonymi AGC/noise/echo → brak noise gate na starcie
 *  - UtteranceEnd → natychmiastowy promote interim na confirmed
 *  - interim → confirmed przy ws.onclose (ostatnia linia obrony)
 *  - Limit 45s → auto-stop
 *
 * Fallback na Web Speech API (silnik `webspeech`, ta sama sesja/`sid`, te same
 * callbacki onSegment/onSpeechFinal/onDone/onError) uruchamia się w DWÓCH
 * sytuacjach:
 *  - `!navigator.onLine` już w momencie warmup() — nie ma sensu w ogóle próbować
 *    Deepgram, przechodzimy od razu na lokalny silnik przeglądarki;
 *  - WS z Deepgram nigdy się nie otworzył (timeout 8s, błąd, wyjątek konstruktora)
 *    — sprzęt (mikrofon) jest już zarezerwowany dla Deepgram, więc najpierw
 *    zwalniamy jego zasoby, dopiero potem Web Speech bierze WŁASNY strumień.
 * Awaria WS PO udanym otwarciu (w trakcie nagrywania) fallbacku NIE wywołuje —
 * zbyt ryzykowne przełączanie silnika w połowie wypowiedzi; wtedy działa
 * istniejąca „ostatnia linia obrony” (interim → confirmed przy onclose).
 */

const WS_PROXY_URL = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/speech/stream?industry=galvo`
const FLUSH_MS = 150    // ms — interwał requestData() (iOS/Android flush)
const MAX_REC_MS = 45000 // ms — limit nagrywania

// ─────────────────────────────────────────────────────────────────────────────
// Android: NIE używać Web Speech API do nagrywania PTT
// (frontend/src/hooks/useDictation.ts)
// ─────────────────────────────────────────────────────────────────────────────
// Na Androidzie `webkitSpeechRecognition` / `SpeechRecognition` NIE jest cichym,
// wbudowanym silnikiem STT jak w Chrome na desktopie. `rec.start()` na
// Androidzie (również w WebView/PWA na tablecie) otwiera NATYWNE, SYSTEMOWE okno
// „Dyktuj" (Google Voice Typing), które:
//   • przejmuje mikrofon i fokus całego WebView — pola tekstowe przestają
//     reagować na dotyk, a klawiatura ekranowa się nie wysuwa,
//   • jest modalne i wybija użytkownika z aplikacji.
// Dlatego na Androidzie ścieżka nagrywania PTT NIE może dotknąć Web Speech.
// Zamiast fallbacku na Web Speech nagrywamy surowe audio lokalnie
// (`MediaRecorder` → Blob → Dexie, store `raw_audio`), a transkrypcję wykonuje
// backend po zsynchronizowaniu nagrania (`offline_pipeline.syncAudio`).
// `MediaRecorder` nie otwiera żadnych okien systemowych.
//
// Wykrycie po `navigator.userAgent` — jedyny pewny sygnał w WebView. Desktop
// oraz iOS zachowują dotychczasowy fallback na Web Speech (tam działa bez okna).
export function prefersLocalAudioCapture(): boolean {
  if (typeof navigator === 'undefined') return false
  return /android/i.test(navigator.userAgent || '')
}

// ─────────────────────────────────────────────────────────────────────────────
// Faza 1 — offline STT na urządzeniu (Vosk WASM)
// ─────────────────────────────────────────────────────────────────────────────
// Model Vosk (~50 MB) + silnik WASM `vosk-browser` żyją poza React — jedna
// instancja na całą kartę, współdzielona przez wszystkie wywołania hooka.
// `ensureVoskModel()` pobiera/inicjuje model RAZ (idempotentnie), a stan
// (`loading`/`ready`/`error`) trafia do `voskModelStore` → `<OfflineModelBanner/>`.
//
// Gałąź `vosk` w `useDictation` uruchamia się WYŁĄCZNIE, gdy w chwili `warmup()`
// jesteśmy offline (`navigator.onLine === false`) I model jest już gotowy. Tor
// online (Deepgram) oraz fallback desktop/iOS (`webspeech`) i Android
// (`localrec`) pozostają nietknięte — model niepobrany ⇒ dokładnie zachowanie
// sprzed Fazy 1 (gwarancja braku regresji).
let voskEngineSingleton: VoskSpeechEngine | null = null
let voskModelLoad: Promise<boolean> | null = null
// Strumień mikrofonu bieżącej sesji offline. Recorder-archiwum trzyma ORYGINAŁ
// (`streamRef`); wstrzyknięty `getUserMedia` Vosk oddaje jego KLON, więc
// `teardownSession()` silnika woła `track.stop()` na klonie i NIE ubija toru
// recordera. `null` poza sesją offline → Vosk (gdyby go wtedy uruchomić) bierze
// własny mikrofon domyślną drogą.
let voskSharedStream: MediaStream | null = null

// ─────────────────────────────────────────────────────────────────────────────
// Krok 3 (FAZA 1) — gramatyka Vosk z `production_dictionaries.ts`
// ─────────────────────────────────────────────────────────────────────────────
// DOMYŚLNIE WYŁĄCZONA — otwarty słownik to bezpieczny przypadek ogólny (swobodna
// notatka). Włącznik per wdrożenie/urządzenie: `localStorage['stt_grammar']==='1'`
// albo `?sttgrammar=1` w URL. Zamknięty słownik (sprzęt + procesy + typowe
// usterki hali) potrafi mocno podnieść trafność w hałasie dla krótkich notatek,
// ale przycina wszystko spoza listy — stąd flaga, nie domyślność. Sam silnik
// degraduje do otwartego słownika, gdy ten build modelu gramatyki nie przyjmie
// (`SpeechDiagnosticEvent` `{type:'grammar', fellBackToOpen:true}`).
function sttGrammarEnabled(): boolean {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('stt_grammar') === '1') return true
  } catch { /* localStorage bywa zablokowany (tryb prywatny) */ }
  try {
    if (typeof location !== 'undefined' && new URLSearchParams(location.search).has('sttgrammar')) return true
  } catch { /* brak location w środowisku testowym */ }
  return false
}

// Frazy do gramatyki Vosk — z zamrożonego `productionDictionaries`, firma `galvo`
// (jedyna wdrożona; `?industry=galvo` jest zakodowane w torze online). Sprzęt +
// synonimy + procesy + produkty + materiały + lokalizacje + typy usterek +
// czynności + wspólne tematy / wzorce ustaleń / powitania. Zdeduplikowane,
// małe litery, wielowyrazowe frazy dozwolone.
export function buildVoskGrammarWords(): string[] {
  const g = productionDictionaries.companies.galvo
  const s = productionDictionaries.shared
  const out = new Set<string>()
  const add = (arr: readonly string[]): void => {
    for (const w of arr) {
      const t = w.trim().toLowerCase()
      if (t) out.add(t)
    }
  }
  add(g.equipment); add(g.processes); add(g.products); add(g.materials)
  add(g.locations); add(g.failureTypes); add(g.actions)
  for (const syns of Object.values(g.equipmentSynonyms)) add(syns)
  for (const syns of Object.values(g.locationSynonyms)) add(syns)
  add(s.topics); add(s.arrangementPatterns); add(s.greetings)
  return [...out]
}

/** `SpeechDiagnosticEvent` → jedna linijka do panelu `<SttDiagnosticsOverlay/>`. */
function formatDiagLine(e: SpeechDiagnosticEvent): string {
  switch (e.type) {
    case 'model-loaded':
      return `model gotowy (+${e.elapsedMs}ms)`
    case 'context-ready':
      return `audio: ctx=${e.inputSampleRate}Hz reco=${e.recognizerSampleRate}Hz${e.downsampling ? ' [downsample→16k]' : ''} (+${e.elapsedMs}ms)`
    case 'grammar':
      return `gramatyka: ${e.mode}${e.wordCount ? ` (${e.wordCount} fraz)` : ''}${e.fellBackToOpen ? ' — FALLBACK do otwartego' : ''}`
    case 'first-audio-signal':
      return `sawAudioSignal=true (+${e.elapsedMs}ms) — mikrofon żyje`
    case 'first-partial':
      return `pierwszy partial (+${e.elapsedMs}ms): "${e.text.slice(0, 40)}"`
    case 'silence-timeout':
      return `WATCHDOG ciszy (+${e.elapsedMs}ms) — brak sygnału, martwy tor audio`
    case 'final':
      return `koniec sesji (+${e.elapsedMs}ms) — transkrypt ${e.chars} zn.`
  }
}

export function getVoskEngine(): VoskSpeechEngine {
  if (!voskEngineSingleton) {
    const grammar = sttGrammarEnabled() ? buildVoskGrammarWords() : undefined
    if (grammar) console.info('[STT-DIAG] gramatyka Vosk ON —', grammar.length, 'fraz')
    voskEngineSingleton = new VoskSpeechEngine({
      modelUrl: VOSK_MODEL_URL,
      grammar,
      onDiagnostic: (event) => {
        console.info('[STT-DIAG]', event)
        sttDiagStore.push(formatDiagLine(event))
      },
      getUserMedia: async (constraints) => {
        const shared = voskSharedStream
        if (shared) return shared.clone()
        return navigator.mediaDevices.getUserMedia(constraints)
      },
    })
  }
  return voskEngineSingleton
}

/**
 * Pobiera / inicjuje model Vosk. Idempotentne (kolejne wywołania współdzielą
 * jedną obietnicę). NIE rzuca: sukces → `true` + store `ready`; błąd → `false` +
 * store `error`, a kolejna próba jest możliwa (np. po odzyskaniu sieci).
 * Pierwsze wywołanie robi dynamiczny `import('vosk-browser')` + `fetch` modelu
 * (`CacheFirst` w SW — potem 100% offline).
 */
function ensureVoskModel(): Promise<boolean> {
  const engine = getVoskEngine()
  if (engine.ready) {
    checkpoint('STT', 'Vosk model already ready')
    voskModelStore.set('ready'); return Promise.resolve(true)
  }
  if (voskModelLoad) {
    checkpoint('STT', 'Vosk model load already in progress')
    return voskModelLoad
  }
  checkpoint('STT', 'Vosk model load starting')
  voskModelStore.set('loading')

  voskModelLoad = engine.load()
    .then(() => {
      checkpoint('STT', 'Vosk model ready');
      voskModelStore.set('ready');
      return true
    })
    .catch((err) => {
      logError('STT', 'Vosk model load failed', err);
      checkpoint('STT', 'Vosk model load failed', { error: String(err) });
      voskModelStore.set('error');
      voskModelLoad = null;
      return false
    })
  return voskModelLoad
}

/**
 * Wyłącznie dla testów — porzuca singleton silnika Vosk i stan pobierania modelu,
 * żeby jeden test-plik nie „zaraził" kolejnych stanem `ready`. Mirror
 * `voskModelStore._reset()`.
 */
export function __resetVoskForTests(): void {
  voskEngineSingleton = null
  voskModelLoad = null
  voskSharedStream = null
}

/**
 * Metadane przekazywane do `onDone` razem z (być może pustym) transkryptem.
 *  - `offlineAudioQueued` — sesja skończyła się bez lokalnej transkrypcji
 *    (Android + offline, silnik `localrec`, ALBO tor Deepgram, w którym WS nigdy
 *    się nie otworzył), ale surowe nagranie trafiło do kolejki `raw_audio` w
 *    Dexie i zostanie zsynchronizowane + przepisane na tekst przez backend po
 *    powrocie sieci. Screen1 pokazuje wtedy od razu kafelek „transkrypcja po
 *    połączeniu", a nie „dyktowanie niedostępne".
 *  - `audioHash` — klucz nagrania w Dexie (`raw_audio`). Screen1 wiąże po nim
 *    optymistyczny kafelek z nagraniem: gdy `syncAudio` wyśle je na serwer i
 *    skasuje lokalną kopię, kafelek-placeholder znika, a na jego miejsce wchodzi
 *    prawdziwa (przepisana) notatka z backendu.
 */
export interface DictationDoneMeta {
  offlineAudioQueued?: boolean
  audioHash?: string
}

interface UseDictationOptions {
  onSegment: (text: string, full: string) => void
  onSpeechFinal: (text: string, full: string) => void
  onDone: (full: string, meta?: DictationDoneMeta) => void
  onError: (msg: string) => void
  onMaxReached?: () => void  // opcjonalny callback przy auto-stop po 45s
}

export function useDictation({ onSegment, onSpeechFinal, onDone, onError, onMaxReached }: UseDictationOptions) {
  // Zasoby audio — globalne (jedna instancja na raz)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Stan sesji
  const activeRef = useRef<boolean>(false)
  const stopRequestedRef = useRef<boolean>(false)
  const cancelledRef = useRef<boolean>(false)
  const currentSidRef = useRef<number>(0)  // globalny licznik sesji
  const activeWsRef = useRef<WebSocket | null>(null)  // WS bieżącej sesji (dla stop())

  // Hybrid STT: który silnik obsługuje BIEŻĄCĄ sesję (`sid`). Fallback zmienia
  // ten ref w trakcie tej samej sesji (bez nowego sid) — patrz `fallbackToWebSpeech`
  // / `fallbackToLocalRecording`.
  //  - 'deepgram'  — chmura (WebSocket + MediaRecorder streaming),
  //  - 'webspeech' — natywny Web Speech API (fallback offline; NIE na Androidzie),
  //  - 'localrec'  — tylko lokalny MediaRecorder → surowe audio do Dexie
  //                  (fallback na Androidzie: bez okna „Dyktuj", transkrypcja po
  //                  stronie backendu po synchronizacji nagrania).
  //  - 'vosk'      — lokalny model Vosk (WASM, w pełni offline) — Faza 1,
  //                  uruchamiany tylko gdy w chwili warmup() jesteśmy offline
  //                  I model jest już pobrany (patrz `ensureVoskModel`).
  const engineRef = useRef<'deepgram' | 'webspeech' | 'localrec' | 'vosk'>('deepgram')
  const recognitionRef = useRef<any>(null)  // instancja webkitSpeechRecognition (silnik `webspeech`)

  // ── Faza 1: offline STT on-device (Vosk WASM) ──────────────────────────────
  // `MediaRecorder` w gałęzi `vosk` ZOSTAJE (archiwum + siatka bezpieczeństwa):
  // pusty wynik Vosk / błąd / watchdog RMS „martwy mikrofon" → nagranie ląduje w
  // kolejce `raw_audio` (`needsTranscription`) i backend przepisze je po sieci —
  // dokładnie dzisiejsze zachowanie toru `localrec`. Najgorszy przypadek =
  // stan sprzed Fazy 1.
  const voskEngineRef = useRef<VoskSpeechEngine | null>(null)
  // Ustawiane w `stop()`: „gdy Vosk odda `onFinal(text)`, domknij sesję offline".
  // `null` = nie prosiliśmy jeszcze o zakończenie; błąd Vosk PRZED `stop()`
  // domyka sesję tą samą drogą (`finishVoskOffline`).
  const voskFinalHandlerRef = useRef<((voskText: string) => void) | null>(null)

  // `onDone` MUSI polecieć dokładnie raz na sesję. Bez tego strażnika tor offline
  // („WS nigdy się nie otworzył" — patrz `stop()`) melduje `onDone` od razu, a
  // ~8 s później phantomowy `ws.onclose` z closure `warmup()` woła `onDone`
  // drugi raz (pusty transkrypt) — Screen1 dostawał podwójny sygnał i kasował
  // świeżo pokazany kafelek. Reset w `warmup()` na starcie każdej sesji.
  const doneEmittedRef = useRef<boolean>(false)

  // Transkrypcja
  const transcriptRef = useRef<string>('')
  const interimRef = useRef<string>('')
  const audioChunksRef = useRef<Blob[]>([])
  // Czy z Deepgram przyszedł JAKIKOLWIEK wynik (`Results` z tekstem) w tej sesji.
  // Rozróżnia „WS się otworzył, ale strumień padł bez ani jednego wyniku"
  // (brak klucza po stronie backendu → close 1011, błąd autoryzacji Deepgram,
  // zerwanie zaraz po otwarciu) od „mieliśmy transkrypt i połączenie zerwało się
  // w trakcie". W pierwszym przypadku nic nie tracimy przełączeniem silnika —
  // robimy Hybrid STT fallback zamiast cicho porzucać sesję (zawieszony mikrofon).
  const gotResultsRef = useRef<boolean>(false)

  // Stabilne referencje do callbacków
  const onSegmentRef = useRef(onSegment)
  const onSpeechFinalRef = useRef(onSpeechFinal)
  const onDoneRef = useRef(onDone)
  const onErrorRef = useRef(onError)
  const onMaxReachedRef = useRef(onMaxReached)
  onSegmentRef.current = onSegment
  onSpeechFinalRef.current = onSpeechFinal
  onDoneRef.current = onDone
  onErrorRef.current = onError
  onMaxReachedRef.current = onMaxReached

  // Jednokrotny `onDone` na sesję — patrz `doneEmittedRef`. Gdy brak `meta`,
  // wołamy z jednym argumentem (zgodność z torami, które nie mają metadanych).
  const emitDone = useCallback((full: string, meta?: DictationDoneMeta) => {
    if (doneEmittedRef.current) {
      logWarn('STT', 'emitDone skipped (already emitted)', { full, meta })
      return
    }
    checkpoint('STT', 'emitDone', { full, meta })
    doneEmittedRef.current = true
    if (meta === undefined) onDoneRef.current(full)
    else onDoneRef.current(full, meta)
  }, [])

  // ── Faza 1: domknięcie sesji offline `vosk` ────────────────────────────────
  // Wołane, gdy Vosk odda `onFinal(text)` (po `stop()`) ALBO gdy silnik zgłosi
  // błąd jeszcze przed `stop()` (watchdog RMS = martwy mikrofon). Zatrzymuje
  // recorder-archiwum, utrwala nagranie i melduje `onDone`:
  //  • Vosk dał tekst   → `emitDone(text)` BEZ meta (parytet z Deepgram: Screen1
  //    → `saveSilentNote(text)` → kafelek z realnymi słowami + `note/create`);
  //    nagranie idzie tylko jako archiwum (`needsTranscription: false`).
  //  • Vosk dał pusto / błąd → `persistAudio(..., true)` + `emitDone('',
  //    { offlineAudioQueued, audioHash })` — backend przepisze po sieci
  //    (dzisiejszy tor `localrec`, zero regresji).
  const finishVoskOffline = useCallback((voskText: string) => {
    checkpoint('STT', 'finishVoskOffline', { text: voskText })
    activeRef.current = false
    if (flushTimerRef.current) { clearInterval(flushTimerRef.current); flushTimerRef.current = null }
    const rec = recorderRef.current
    recorderRef.current = null
    const text = voskText.trim()

    const settle = (hash: string | null) => {
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null }
      if (cancelledRef.current) { sttDiagStore.push('sesja anulowana (krótki tap)'); return }
      if (text) {
        sttDiagStore.setTranscript(text)
        sttDiagStore.push(`WYNIK: notatka z ${text.length} zn. (parytet online)`)
        emitDone(text)
      } else {
        sttDiagStore.push(hash ? 'WYNIK: pusto → nagranie w kolejce, backend przepisze po sieci' : 'WYNIK: pusto, brak nagrania')
        emitDone('', hash ? { offlineAudioQueued: true, audioHash: hash } : undefined)
      }
    }

    const persistThen = () => {
      persistAudio(audioChunksRef.current, rec?.mimeType || '', /* needsTranscription */ !text)
        .then(hash => settle(text ? null : hash))
        .catch(() => settle(null))
    }

    if (rec && rec.state !== 'inactive') {
      rec.onstop = () => { if (cancelledRef.current) settle(null); else persistThen() }
      try { rec.stop() } catch (_) { settle(null) }
    } else if (audioChunksRef.current.length && !cancelledRef.current) {
      persistThen()
    } else {
      settle(null)
    }
  }, [emitDone])

  // ── Silnik `webspeech` — fallback offline (Web Speech API) ──────────────
  // Ta sama sesja (`sid`) co Deepgram — patrz komentarz na górze pliku.
  const startWebSpeech = useCallback((sid: number) => {
    if (sid !== currentSidRef.current || cancelledRef.current) return
    engineRef.current = 'webspeech'
    activeRef.current = true
    transcriptRef.current = ''
    interimRef.current = ''

    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SR) {
      activeRef.current = false
      onErrorRef.current('Brak dostępu do dyktowania offline (Web Speech API niedostępne)')
      return
    }

    console.log(`[STT] Web Speech start sid=${sid}`)
    const rec = new SR()
    rec.lang = 'pl-PL'
    rec.continuous = true
    rec.interimResults = true
    recognitionRef.current = rec

    rec.onresult = (e: any) => {
      if (sid !== currentSidRef.current) return
      const finals: string[] = []
      let interim = ''
      for (let i = 0; i < e.results.length; i++) {
        const t = e.results[i][0]?.transcript ?? ''
        if (e.results[i].isFinal) finals.push(t)
        else interim = t
      }
      transcriptRef.current = mergeFinalSegments(finals)
      interimRef.current = interim
      const full = (transcriptRef.current + (interim ? ' ' + interim : '')).trim()
      onSegmentRef.current(full, full)
    }

    rec.onerror = (e: any) => {
      if (sid !== currentSidRef.current || cancelledRef.current) return
      // 'no-speech'/'aborted' to normalne zakończenie ciszy/przerwania — onend
      // i tak dobije transkrypt; realny błąd (np. brak uprawnień) zgłaszamy dalej.
      if (e?.error !== 'no-speech' && e?.error !== 'aborted') {
        onErrorRef.current(`Dyktowanie offline: ${e?.error ?? 'nieznany błąd'}`)
      }
    }

    rec.onend = () => {
      recognitionRef.current = null
      if (sid !== currentSidRef.current) return
      activeRef.current = false
      const full = (transcriptRef.current + (interimRef.current ? ' ' + interimRef.current : '')).trim()
      transcriptRef.current = ''
      interimRef.current = ''
      if (cancelledRef.current) return
      const final = collapseRepeatedPhrases(full)
      if (final) onSpeechFinalRef.current(final, final)
      emitDone(final)
    }

    try {
      rec.start()
    } catch (e: any) {
      activeRef.current = false
      recognitionRef.current = null
      onErrorRef.current(`Start dyktowania offline: ${e?.message ?? 'błąd'}`)
    }
  }, [])

  // ── Faza 1: start silnika offline `vosk` na tej samej sesji (`sid`) ─────────
  // Wołane z `warmup()` PO uruchomieniu recordera-archiwum. Vosk prowadzi WŁASNY
  // strumień mikrofonu (klon `streamRef` — patrz `voskSharedStream`), z DSP ON
  // dziedziczonym po oryginale. `onPartial` → `onSegment` (żywy tekst). Wynik
  // domyka `finishVoskOffline` (przez `voskFinalHandlerRef` po `stop()` albo
  // wprost — gdy silnik zgłosi błąd terminalny przed `stop()`).
  const startVoskOffline = useCallback((sid: number) => {
    if (sid !== currentSidRef.current || cancelledRef.current) return
    const shared = streamRef.current
    if (!shared) { finishVoskOffline(''); return }
    engineRef.current = 'vosk'
    voskFinalHandlerRef.current = null
    const engine = getVoskEngine()
    voskEngineRef.current = engine
    voskSharedStream = shared
    console.log(`[STT] Vosk offline start sid=${sid}`)
    sttDiagStore.push(`start sesji Vosk (sid=${sid})`)
    sttDiagStore.setTranscript('')

    checkpoint('STT', 'Vosk engine.start called', { sid })
    void engine.start({
      onPartial: (liveText: string) => {
        if (sid !== currentSidRef.current || cancelledRef.current) return
        checkpoint('STT', 'Vosk onPartial', { text: liveText, sid })
        transcriptRef.current = liveText
        sttDiagStore.setTranscript(liveText)
        onSegmentRef.current(liveText, liveText)
      },
      onSegmentFinal: (segText: string, fullText: string) => {
        if (sid !== currentSidRef.current || cancelledRef.current) return
        checkpoint('STT', 'Vosk segment final', { segText, fullText, sid })
        onSpeechFinalRef.current(segText, fullText)
      },
      onFinal: (fullText: string) => {
        checkpoint('STT', 'Vosk onFinal', { text: fullText, sid })
        if (sid !== currentSidRef.current) return
        const handler = voskFinalHandlerRef.current
        voskFinalHandlerRef.current = null
        voskEngineRef.current = null
        if (handler) handler(fullText)
        else if (!cancelledRef.current) finishVoskOffline(fullText)
      },
      onError: (msg: string) => {
        logError('STT', 'Vosk onError', { msg, sid })
        if (sid !== currentSidRef.current || cancelledRef.current) return
        onErrorRef.current(msg)
        const handler = voskFinalHandlerRef.current
        voskFinalHandlerRef.current = null
        voskEngineRef.current = null
        // Błąd Vosk jest terminalny (NIE ma po nim `onFinal`). Domknij sesję jak
        // tor lokalny: nagranie-archiwum → kolejka `raw_audio`, backend przepisze.
        if (handler) handler('')
        else finishVoskOffline('')
      },
    }).finally(() => { checkpoint('STT', 'Vosk engine.start promise settled', { sid }); voskSharedStream = null })
  }, [finishVoskOffline])

  // Android: Deepgram niedostępny → zostań na `MediaRecorder`, który JUŻ nagrywa.
  // Zrywamy tylko WebSocket do chmury; `ondataavailable` dalej dopisuje chunki do
  // `audioChunksRef`, a `stop()` utrwali je w Dexie (`raw_audio`). Żadnego
  // `SpeechRecognition`, żadnego natywnego okna „Dyktuj" (patrz
  // `prefersLocalAudioCapture`). Idempotentne — strażnik `!== 'deepgram'`.
  const fallbackToLocalRecording = useCallback((sid: number, reason: string) => {
    if (sid !== currentSidRef.current || cancelledRef.current || stopRequestedRef.current) return
    if (engineRef.current !== 'deepgram') return
    console.log(`[STT] Deepgram niedostępny (${reason}) → lokalne nagrywanie MediaRecorder (bez Web Speech) sid=${sid}`)
    engineRef.current = 'localrec'
    activeRef.current = true
    // recorder + stream + flushTimer ZOSTAJĄ nietknięte — nagrywanie trwa dalej,
    // tyle że chunki lądują wyłącznie w buforze (brak otwartego WS do wysyłki).
  }, [])

  // Deepgram nigdy się nie połączył (timeout/błąd/wyjątek) — zwolnij zasoby
  // audio zarezerwowane dla niego i przełącz TĘ SAMĄ sesję na Web Speech.
  // Idempotentne: strażnik `engineRef.current !== 'deepgram'` chroni przed
  // podwójnym fallbackiem, gdy timeout i `ws.onerror` trafią się oba naraz.
  const fallbackToWebSpeech = useCallback((sid: number, reason: string) => {
    if (sid !== currentSidRef.current || cancelledRef.current || stopRequestedRef.current) return
    if (engineRef.current !== 'deepgram') return
    // Android: NIE wołaj Web Speech (natywne okno „Dyktuj") — zostań na MediaRecorder.
    if (prefersLocalAudioCapture()) { fallbackToLocalRecording(sid, reason); return }
    console.log(`[STT] Deepgram niedostępny (${reason}) → fallback Web Speech sid=${sid}`)

    if (flushTimerRef.current) { clearInterval(flushTimerRef.current); flushTimerRef.current = null }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.onstop = null; recorderRef.current.stop() } catch (_) { }
    }
    recorderRef.current = null
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null }
    audioChunksRef.current = []

    startWebSpeech(sid)
  }, [startWebSpeech, fallbackToLocalRecording])

  const warmup = useCallback(async () => {
    // Zwolnij zasoby audio poprzedniej sesji (WS zostaje — dobiega sam w tle)
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop() } catch (_) { }
    }
    recorderRef.current = null
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null }
    if (flushTimerRef.current) { clearInterval(flushTimerRef.current); flushTimerRef.current = null }
    if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null }
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null
        recognitionRef.current.onerror = null
        recognitionRef.current.onend = null
        recognitionRef.current.abort()
      } catch (_) { }
      recognitionRef.current = null
    }
    // Faza 1: porzuć niedokończoną sesję offline `vosk` z poprzedniego dotknięcia
    // (silnik `cancel()` = bez `onFinal`, model ZOSTAJE w pamięci na kolejny raz).
    if (voskEngineRef.current) {
      try { voskEngineRef.current.cancel() } catch (_) { }
      voskEngineRef.current = null
    }
    voskFinalHandlerRef.current = null
    voskSharedStream = null

    // Lokalny WS dla tej sesji — żyje w closure, stary WS dobiega w tle
    // (`sendClose()` trzyma do niego własną referencję i zamknie go po 6 s).
    // `activeWsRef` zerujemy: od teraz `!activeWsRef.current` znaczy jednoznacznie
    // „WS TEJ sesji jeszcze się nie otworzył" — na tym opiera się offline'owa
    // gałąź `stop()` (nie chcemy jej pomylić ze STARYM, dogasającym WS).
    let ws: WebSocket | null = null
    activeWsRef.current = null
    const sid = ++currentSidRef.current
    engineRef.current = 'deepgram'

    activeRef.current = true
    stopRequestedRef.current = false
    cancelledRef.current = false
    doneEmittedRef.current = false
    transcriptRef.current = ''
    interimRef.current = ''
    audioChunksRef.current = []
    gotResultsRef.current = false

    // Faza 1: pobierz/inicjuj model Vosk RAZ (idempotentne, nie blokuje). Pierwsze
    // dyktowanie na Wi-Fi prefetchuje ~50 MB (CacheFirst) → kolejne sesje bez
    // sieci pojadą już on-device. Stan → `<OfflineModelBanner/>`.
    void ensureVoskModel()

    // Android + offline: NIE wołaj Web Speech (natywne okno „Dyktuj") — nagraj
    // surowe audio lokalnie (MediaRecorder → Dexie). Nie ma po co próbować WS do
    // Deepgram; startujemy sam recorder w trybie „tylko lokalny zapis" (flaga
    // `localRecOnly` niżej pomija tworzenie WebSocketu).
    let localRecOnly = false
    // Faza 1: offline + pobrany model Vosk → rozpoznawanie mowy on-device na tej
    // sesji (recorder-archiwum i tak startuje niżej jako siatka bezpieczeństwa).
    let voskOffline = false

    // Hybrid STT: offline już na starcie → nie ma sensu próbować Deepgram
    // (8s timeoutu, po którym i tak trzeba by fallbacku).
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      if (getVoskEngine().ready) {
        console.log(`[STT] offline + model Vosk gotowy → STT on-device sid=${sid}`)
        voskOffline = true
      } else if (prefersLocalAudioCapture()) {
        // Model jeszcze niepobrany — Android: nagrywaj lokalnie (bez Web Speech).
        console.log(`[STT] Android+offline (model Vosk niegotowy) → MediaRecorder sid=${sid}`)
        localRecOnly = true
      } else {
        // Model niepobrany, desktop/iOS: dotychczasowy fallback Web Speech.
        console.log(`[STT] offline (model Vosk niegotowy) → Web Speech od startu sid=${sid}`)
        startWebSpeech(sid)
        return
      }
    }

    const t0 = performance.now()
    console.log(`[DG] warmup() sid=${sid}`)

    // ── 1. getUserMedia ──
    // Tor Deepgram: filtry systemowe WYŁĄCZONE (brak noise gate na starcie).
    // Tor Vosk offline (`voskOffline`): filtry WŁĄCZONE — mały model + hałas hali
    // = DSP pomaga (Vosk czyta klon tego strumienia, więc dziedziczy DSP ON).
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: voskOffline
          ? { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 }
          : { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 1 },
      })
      streamRef.current = stream
      console.log(`[DG] mic OK T=${(performance.now() - t0).toFixed(0)}ms`)
    } catch (e: any) {
      if (sid !== currentSidRef.current) return
      activeRef.current = false
      onErrorRef.current(`Mikrofon: ${e.message}`)
      return
    }

    if (cancelledRef.current || sid !== currentSidRef.current) {
      stream.getTracks().forEach(t => t.stop())
      streamRef.current = null
      activeRef.current = false
      return
    }

    // ── 2. MediaRecorder bez timeslice + requestData() co FLUSH_MS ──
    const ramBuf: Blob[] = []
    let wsReady = false

    const mimeType = getSupportedMime()
    let recorder: MediaRecorder
    try {
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
    } catch (e: any) {
      activeRef.current = false
      stream.getTracks().forEach(t => t.stop()); streamRef.current = null
      onErrorRef.current(`MediaRecorder: ${e.message}`)
      return
    }
    recorderRef.current = recorder

    recorder.ondataavailable = (e) => {
      if (!e.data || e.data.size === 0 || cancelledRef.current) return
      audioChunksRef.current.push(e.data)
      if (wsReady && ws && ws.readyState === WebSocket.OPEN) {
        ws.send(e.data)
      } else {
        ramBuf.push(e.data)
      }
    }
    recorder.onerror = () => { }
    recorder.start()  // bez timeslice — kontrolujemy przez requestData()

    // Wymuś flush co FLUSH_MS (iOS/Android fix)
    flushTimerRef.current = setInterval(() => {
      if (recorder.state === 'recording') recorder.requestData()
    }, FLUSH_MS)

    console.log(`[DG] recorder started T=${(performance.now() - t0).toFixed(0)}ms mimeType=${recorder.mimeType}`)

    // ── Faza 1: offline STT on-device (Vosk WASM) ──
    // Recorder-archiwum już buforuje audio; Vosk rozpoznaje mowę z KLONU
    // strumienia. Wynik domknie `stop()` (→ `finishVoskOffline`).
    if (voskOffline) {
      console.log(`[STT] vosk — start silnika offline sid=${sid} T=${(performance.now() - t0).toFixed(0)}ms`)
      startVoskOffline(sid)
      return
    }

    // Android + offline (patrz `localRecOnly` wyżej): pomijamy WebSocket i Web
    // Speech — sam MediaRecorder buforuje audio, `stop()` utrwali je w Dexie.
    if (localRecOnly) {
      engineRef.current = 'localrec'
      console.log(`[STT] localrec — pomijam WebSocket, tylko MediaRecorder sid=${sid} T=${(performance.now() - t0).toFixed(0)}ms`)
      return
    }

    // ── 3. WebSocket — lokalny dla tej sesji ──
    try {
      ws = new WebSocket(WS_PROXY_URL)
    } catch (e: any) {
      fallbackToWebSpeech(sid, `wsInitThrow:${e.message}`)
      return
    }

    const openTimeout = setTimeout(() => {
      if (ws && ws.readyState !== WebSocket.OPEN) {
        ws.close()
        fallbackToWebSpeech(sid, 'timeoutOpen8s')
      }
    }, 8000)

    // ── 4. ws.onopen — flush ramBuf (nagłówek kontenera zawsze pierwszy) ──
    ws.onopen = () => {
      clearTimeout(openTimeout)
      activeWsRef.current = ws
      console.log(`[DG] WS open T=${(performance.now() - t0).toFixed(0)}ms buf=${ramBuf.length} sid=${sid}`)
      if (cancelledRef.current || stopRequestedRef.current) { ws!.close(1000); return }
      ramBuf.forEach(blob => ws!.send(blob))
      ramBuf.length = 0
      wsReady = true
    }

    // ── 5. onmessage — tylko dla bieżącej sesji I dopóki silnik to Deepgram ──
    ws.onmessage = (ev) => {
      if (sid !== currentSidRef.current || cancelledRef.current || engineRef.current !== 'deepgram') return
      let msg: any
      try { msg = JSON.parse(ev.data) } catch { return }

      if (msg.type === 'Results') {
        const text = (msg.channel?.alternatives?.[0]?.transcript ?? '').trim()
        if (!text) return
        gotResultsRef.current = true

        if (msg.is_final) {
          transcriptRef.current = transcriptRef.current ? transcriptRef.current + ' ' + text : text
          interimRef.current = ''
        } else {
          interimRef.current = text
        }

        const full = interimRef.current
          ? (transcriptRef.current ? transcriptRef.current + ' ' + interimRef.current : interimRef.current)
          : transcriptRef.current

        onSegmentRef.current(text, full)

        if (msg.speech_final && transcriptRef.current) {
          onSpeechFinalRef.current(text, transcriptRef.current)
        }
      } else if (msg.type === 'UtteranceEnd') {
        // Deepgram wykrył ciszę — jeśli zostaje interim, promuj na confirmed natychmiast
        if (interimRef.current) {
          transcriptRef.current = transcriptRef.current
            ? transcriptRef.current + ' ' + interimRef.current
            : interimRef.current
          interimRef.current = ''
          const full = transcriptRef.current
          onSegmentRef.current('', full)
          onSpeechFinalRef.current('', full)
        }
      }
    }

    ws.onerror = () => {
      clearTimeout(openTimeout)
      if (sid !== currentSidRef.current || cancelledRef.current || engineRef.current !== 'deepgram') return
      if (!wsReady) {
        // Nigdy się nie połączyło — Hybrid STT: przejmuje Web Speech.
        fallbackToWebSpeech(sid, 'wsError')
      } else {
        // Zerwane W TRAKCIE nagrywania — zbyt ryzykowne przełączanie silnika
        // w połowie wypowiedzi; „ostatnia linia obrony” w onclose i tak
        // dobije to, co już mamy (interim → confirmed).
        onErrorRef.current('Błąd połączenia z Deepgram')
      }
    }

    ws.onclose = () => {
      clearTimeout(openTimeout)
      if (activeWsRef.current === ws) activeWsRef.current = null

      // Tor offline w `stop()` („WS nigdy się nie otworzył") już zameldował
      // `onDone` i zakolejkował nagranie — ten phantomowy `onclose` z closure
      // `warmup()` (WS w końcu dobiegł / `openTimeout` go zamknął) jest już
      // tylko no-opem.
      if (doneEmittedRef.current) return

      // Tylko bieżąca sesja aktualizuje stan i wywołuje callbacki — i tylko
      // dopóki sesja wciąż należy do Deepgram (fallback mógł ją już przenieść
      // na Web Speech, wtedy ten WS jest już nieaktualny i onclose to no-op).
      if (sid !== currentSidRef.current || engineRef.current !== 'deepgram') return

      activeRef.current = false
      // Ostatnia linia obrony — interim który nie dostał is_final
      if (interimRef.current) {
        transcriptRef.current = transcriptRef.current
          ? transcriptRef.current + ' ' + interimRef.current
          : interimRef.current
        interimRef.current = ''
      }

      const final = transcriptRef.current.trim()
      transcriptRef.current = ''
      interimRef.current = ''

      if (cancelledRef.current) {
        console.log(`[DG] sid=${sid} cancelled, skipping onDone`)
        return
      }
      if (stopRequestedRef.current || final) {
        console.log(`[DG] sid=${sid} calling onDone with:`, final.slice(0, 50))
        emitDone(final)
      } else if (!gotResultsRef.current) {
        // WS „otworzył się" (proxy przepuścił handshake), ale strumień zamknął
        // się bez ANI JEDNEGO wyniku, a użytkownik wciąż trzyma przycisk. Powody:
        // backend bez DEEPGRAM_API_KEY (close 1011), błąd autoryzacji Deepgram,
        // zerwanie zaraz po otwarciu. Cicha rezygnacja = zawieszony mikrofon i
        // pusta notatka, więc traktujemy to jak nieudane otwarcie → Hybrid STT
        // (desktop/iOS: Web Speech; Android: dalszy lokalny zapis MediaRecorder).
        console.log(`[DG] sid=${sid} WS zamknięty bez żadnego wyniku → fallback`)
        fallbackToWebSpeech(sid, 'closedNoResults')
      } else {
        console.log(`[DG] sid=${sid} no final text and not stopped, skipping onDone`)
      }
    }
  }, [])

  const start = useCallback(async () => {
    if (!activeRef.current) return warmup()
    // Warmup już aktywny — ustaw limit 45s
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current)
    maxTimerRef.current = setTimeout(() => {
      onMaxReachedRef.current?.()
      stop()
    }, MAX_REC_MS)
  }, [warmup])

  const cancel = useCallback(() => {
    cancelledRef.current = true
    activeRef.current = false
    stopRequestedRef.current = false

    if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null }

    // Hybrid STT: silnik `webspeech` ma własny cykl życia (bez WS/recorder) —
    // przerwij go i wyjdź, żeby nie wpaść w sprzątanie zasobów Deepgram niżej.
    // Silnik `localrec` (Android) używa tego samego recordera co Deepgram, więc
    // CELOWO spada do sprzątania niżej — recorder zatrzymany, audio porzucone
    // (cancel = krótki tap, bez zapisu), bez `onDone`.
    if (engineRef.current === 'webspeech') {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.onresult = null
          recognitionRef.current.abort()
        } catch (_) { }
        recognitionRef.current = null
      }
      transcriptRef.current = ''
      interimRef.current = ''
      return
    }

    // Faza 1: silnik `vosk` — przerwij BEZ `onFinal` (krótki tap). Vosk zamyka
    // WŁASNY klon strumienia sam; recorder-archiwum + oryginalny strumień
    // sprząta wspólny tor niżej (audio porzucone — cancel = bez zapisu).
    if (engineRef.current === 'vosk') {
      voskFinalHandlerRef.current = null
      try { voskEngineRef.current?.cancel() } catch (_) { }
      voskEngineRef.current = null
      voskSharedStream = null
      audioChunksRef.current = []
    }

    if (flushTimerRef.current) { clearInterval(flushTimerRef.current); flushTimerRef.current = null }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop() } catch (_) { }
    }
    recorderRef.current = null
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null }
    // WS lokalny w closure — nie mamy do niego dostępu stąd, zamknie się sam przez onclose
    transcriptRef.current = ''
    interimRef.current = ''
  }, [])

  const stop = useCallback(() => {
    checkpoint('STT', 'stop called', { engine: engineRef.current, active: activeRef.current, cancelled: cancelledRef.current })
    if (cancelledRef.current || !activeRef.current) return
    stopRequestedRef.current = true
    activeRef.current = false

    if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null }

    // Hybrid STT: `webspeech` kończy się gracefully przez rec.stop() → rec.onend
    // (tam liczony jest finalny transkrypt i wołane onDone) — bez WS/recordera.
    if (engineRef.current === 'webspeech') {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop() } catch (_) { }
      }
      return
    }

    // ── Faza 1: silnik `vosk` (offline, on-device) ──
    // Domknij wypowiedź w silniku; gdy odda `onFinal(text)`, `finishVoskOffline`
    // utrwali nagranie-archiwum i zamelduje `onDone`:
    //  • tekst z Vosk → `emitDone(text)` (parytet z online),
    //  • pusto / błąd → nagranie do kolejki `raw_audio` + `onDone('', meta)`
    //    (dzisiejszy tor `localrec` — zero regresji).
    // MUSI być PRZED gałęzią `!activeWsRef.current` niżej (tor `vosk` nie ma WS).
    if (engineRef.current === 'vosk') {
      const engine = voskEngineRef.current
      voskEngineRef.current = null
      // Silnik już zdjęty przez własny `onError` → `finishVoskOffline` domknął
      // sesję (`activeRef=false` → i tak nie doszlibyśmy tu). Defensywnie: no-op.
      if (!engine) return
      voskFinalHandlerRef.current = (voskText: string) => finishVoskOffline(voskText)
      try { engine.stop() } catch (_) { finishVoskOffline('') }
      return
    }

    // ── Tor lokalny bez transkrypcji: silnik `localrec` (Android offline) LUB
    // tor Deepgram, w którym WS NIGDY się nie otworzył (`!activeWsRef.current` —
    // np. Wi-Fi bez internetu, serwer nieosiągalny, a użytkownik puścił przycisk
    // przed 8-sekundowym fallbackiem). W obu przypadkach: domknij nagrywanie,
    // utrwal surowe audio w Dexie (`raw_audio`, `needsTranscription: true` →
    // `offline_pipeline.syncAudio` wyśle je na backend do przepisania) i ZARAZ
    // zamelduj `onDone('', { offlineAudioQueued, audioHash })`. Wcześniej ten
    // drugi przypadek szedł gałęzią `sendClose()` niżej i `onDone('')` przychodziło
    // dopiero po ~8 s przez phantomowy `ws.onclose`, bez `meta` — Screen1 nie miał
    // jak pokazać ani kafelka, ani bannera („brak reakcji offline"). ────────────
    if (engineRef.current === 'localrec' || !activeWsRef.current) {
      checkpoint('STT', 'stop → localrec fallback')
      engineRef.current = 'localrec'
      if (flushTimerRef.current) { clearInterval(flushTimerRef.current); flushTimerRef.current = null }
      const rec = recorderRef.current
      recorderRef.current = null
      // `hash` (klucz w Dexie `raw_audio`) niepuste = nagranie realnie zapisane →
      // backend przepisze je na tekst po synchronizacji, a Screen1 wiąże po nim
      // optymistyczny kafelek. `null` = nic nie nagrano / zapis się nie powiódł.
      const finish = (hash: string | null) => {
        if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null }
        if (!cancelledRef.current) {
          emitDone('', hash ? { offlineAudioQueued: true, audioHash: hash } : undefined)
        }
      }
      if (rec && rec.state !== 'inactive') {
        rec.onstop = () => {
          if (cancelledRef.current) { finish(null); return }
          persistAudio(audioChunksRef.current, rec.mimeType || '', true)
            .then(finish)
            .catch(() => finish(null))
        }
        try { rec.stop() } catch (_) { finish(null) }
      } else {
        finish(null)
      }
      return
    }

    if (flushTimerRef.current) { clearInterval(flushTimerRef.current); flushTimerRef.current = null }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null }

    // CloseStream przez recorder.onstop — gwarantuje że ostatni chunk już wyszedł
    const wsRef = activeWsRef.current
    const sendClose = () => {
      if (wsRef && wsRef.readyState === WebSocket.OPEN) {
        wsRef.send(JSON.stringify({ type: 'CloseStream' }))
      }
      // WS zamknie się sam po 6s w tle — nie blokuje UI
      setTimeout(() => {
        if (wsRef && wsRef.readyState !== WebSocket.CLOSED) wsRef.close(1000)
      }, 6000)
    }

    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      const rec = recorderRef.current
      rec.onstop = () => {
        if (!cancelledRef.current) {
          persistAudio(audioChunksRef.current, rec.mimeType || '').catch(() => { })
        }
        sendClose()
      }
      rec.stop()
    } else {
      sendClose()
    }
    recorderRef.current = null
  }, [])

  return { warmup, start, stop, cancel }
}

function getSupportedMime(): string | null {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
  ]
  return types.find(t => MediaRecorder.isTypeSupported(t)) ?? null
}

// Zwraca `hash` (klucz w Dexie `raw_audio`), gdy nagranie faktycznie zapisano —
// wtedy backend przepisze je na tekst po synchronizacji, a Screen1 wiąże po tym
// kluczu optymistyczny kafelek notatki z nagraniem. `null` = nie było czego
// zapisać albo zapis się nie powiódł (brak pewności, że notatka powstanie).
//
// `needsTranscription` — nagranie zrobione OFFLINE bez lokalnej transkrypcji.
// Backend po zsynchronizowaniu ma je przepisać i utworzyć notatkę na E1. Tor
// online (transkrypt już mamy z Deepgram) zapisuje BEZ tej flagi — tam upload
// to wyłącznie archiwum.
async function persistAudio(chunks: Blob[], mimeType: string, needsTranscription = false): Promise<string | null> {
  if (!chunks.length) return null
  try {
    const blob = new Blob(chunks, { type: mimeType || 'audio/webm' })
    const buf = await blob.arrayBuffer()
    const digest = await crypto.subtle.digest('SHA-256', buf)
    const hash = Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('')
    await saveRawAudio(hash, blob, Date.now(), undefined, needsTranscription)
    console.log('[DG] raw audio saved', hash, blob.size, needsTranscription ? '(needsTranscription)' : '')
    return hash
  } catch (e: any) {
    console.warn('[DG] persistAudio failed', e.message)
    return null
  }
}
